import React, { useEffect, useState } from "react";
import PortalLayout from "../components/PortalLayout";
import { Button } from "../components/ui/button";
import { certApi } from "../lib/api";
import { portalApi } from "../lib/portalApi";
import { toast } from "sonner";
import { GraduationCap, Award, Download } from "lucide-react";

const Inner = () => {
  const [tracks, setTracks] = useState([]);
  const [enrollment, setEnrollment] = useState(null);

  const load = () => portalApi.certMe().then(r => setEnrollment(r.enrollment));
  useEffect(() => {
    certApi.tracks().then(r => setTracks(r.tracks || []));
    load();
  }, []);

  const enroll = async (track) => {
    try { const r = await portalApi.certEnroll(track); setEnrollment(r.enrollment); toast.success("Enrolled"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enrollment failed"); }
  };

  const download = async () => {
    try {
      const md = await portalApi.certCertificate();
      const blob = new Blob([md], { type: "text/markdown" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `auditiq-certificate.md`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error("No enrollment — enroll first"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Certification</div>
        <h1 className="font-display text-3xl md:text-4xl mt-2">Training & Certification</h1>
        <p className="text-sm text-cream-dim mt-1">
          Enroll in an AuditIQ track, track your progress, and download a certificate. No-cost pathways available for eligible participants.
        </p>
      </div>

      {enrollment && (
        <div className="glass-accent p-6" data-testid="enrollment-card">
          <div className="flex items-center gap-3">
            <Award size={24} className="text-electric" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-electric">Current Enrollment</div>
              <h3 className="font-display text-xl">{tracks.find(t => t.key === enrollment.track)?.title || enrollment.track}</h3>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-cream-dim mb-1">
              <span>Progress</span><span>{enrollment.progress_pct}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden bg-navy-deep">
              <div style={{ width: `${enrollment.progress_pct}%` }} className="h-full bg-gold transition-all" />
            </div>
          </div>
          <ul className="mt-5 space-y-2 text-sm">
            {enrollment.modules.map((m, i) => (
              <li key={i} className="flex items-center justify-between border-b border-gold-dim/10 pb-2">
                <span className="text-cream-dim">{m.name}</span>
                <span className={`text-[10px] uppercase tracking-wider ${m.completed ? "text-[color:var(--auditiq-success)]" : "text-steel"}`}>
                  {m.completed ? "Complete" : "Pending"}
                </span>
              </li>
            ))}
          </ul>
          <Button onClick={download} className="btn-gold mt-5 h-10 px-5" data-testid="cert-download">
            <Download size={14} className="mr-2" /> Download Certificate (Markdown)
          </Button>
        </div>
      )}

      <div>
        <h3 className="font-display text-xl mb-4">Available tracks</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {tracks.map(t => (
            <div key={t.key} className="glass p-5" data-testid={`portal-track-${t.key}`}>
              <GraduationCap size={22} className="text-gold" />
              <div className="font-display text-lg mt-3">{t.title}</div>
              <div className="text-xs text-steel mt-1">{t.hours} hours · {t.audience}</div>
              <Button onClick={() => enroll(t.key)} className="btn-ghost-gold h-10 mt-4 px-4"
                data-testid={`portal-enroll-${t.key}`}>
                {enrollment?.track === t.key ? "Currently enrolled" : "Enroll"}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PortalCertification = () => <PortalLayout><Inner /></PortalLayout>;
export default PortalCertification;
